import { useCallback, useRef, useState } from "react";

export type FieldErrorMap = Record<string, string | null>;

/**
 * Shared form-error behaviour for the multi-step forms.
 *
 * `validate` is a pure function of the form data returning one entry per
 * checked field. Errors are only ever surfaced by `validateAll` (on submit);
 * `revalidate` runs on every edit but can only clear or refresh a message that
 * is already on screen. That way nothing turns red while the user is still
 * filling the form, and a message disappears as soon as the value is valid —
 * without waiting for another submit.
 */
export const useFieldErrors = <T,>(validate: (data: T) => FieldErrorMap) => {
  const [errors, setErrors] = useState<FieldErrorMap>({});

  // Kept in a ref so callers don't have to memoise their validator.
  const validateRef = useRef(validate);
  validateRef.current = validate;

  /** Runs every check and shows all failures. Returns true when the form is valid. */
  const validateAll = useCallback((data: T) => {
    const next = validateRef.current(data);
    setErrors(next);
    return Object.values(next).every((message) => !message);
  }, []);

  /** Re-checks only the fields whose error is currently visible. */
  const revalidate = useCallback((data: T) => {
    setErrors((prev) => {
      const visible = Object.keys(prev).filter((field) => prev[field]);
      if (!visible.length) {
        return prev;
      }

      const fresh = validateRef.current(data);
      const next = { ...prev };
      visible.forEach((field) => {
        next[field] = fresh[field] ?? null;
      });

      return next;
    });
  }, []);

  return { errors, validateAll, revalidate };
};
